import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { useTheme } from '@mui/material/styles';
import { IconTrash } from '@tabler/icons-react';

import {
  Button,
  Chip,
  IconButton,
  SelectDropdown,
  TextField,
  Tooltip,
  Typography,
} from '@zendesk/forethought-elements';
import { useDebouncedUpdateAbTestConfig, useGetAgents } from './hooks';
import {
  buildControlVariantUpdate,
  generateAbTestConfigRequestBody,
  getIsDuplicateVariantVersion,
  getIsTotalTrafficPercentageValid,
  getVariantVersionLabel as getVariantVersionLabelUtil,
  getVersionHistoryDataForIntentWorkflowId,
  parseTrafficPercentage,
} from './utils';
import { useAbTestEligibility } from 'src/hooks/useAbTestEligibility';
import { VersionSelectDropdown } from 'src/pages/workflow-builder-edit/autonomous-agent/new-version-history/VersionSelectDropdown';
import { useGetAbTestConfigByIdQuery } from 'src/services/agent-ab-test/agentAbTestApi';
import {
  SolveABTestVariant,
  VARIANT_DEFAULT_NAMES,
} from 'src/services/agent-ab-test/types';
import { useGetAllAutoflowVersionHistoriesV2Query } from 'src/services/workflowBuilderAutoflowApi.ts/workflowBuilderAutoflowApi';
import { WorkflowVersionType } from 'src/slices/workflow-preview/types';
import { SolveWidgetProduct } from 'src/types/types';

// Variant indices in nonControlVariants array
const VARIANT_A_INDEX = 0;
const VARIANT_B_INDEX = 1;

const AgentVariantsSection = () => {
  const { t } = useTranslation('agent-ab-test');
  const { palette } = useTheme();
  const { abTestId = '' } = useParams();

  const { data } = useGetAbTestConfigByIdQuery(abTestId, {
    skip: !abTestId,
  });

  const {
    intent_workflow_id = '',
    product,
    status,
    variants = [],
  } = data || {};

  const isEditingDisabled = status !== 'draft';

  const primaryProduct: SolveWidgetProduct = product || 'workflow_builder';
  const agents = useGetAgents(primaryProduct);

  const { getEligibility, isLoading: isCheckingVersionHistories } =
    useAbTestEligibility();

  // Version histories are still needed for building version dropdowns and
  // looking up published version data (separate from the eligibility check).
  const { data: allVersionHistories } =
    useGetAllAutoflowVersionHistoriesV2Query();

  const agentOptions = useMemo(
    () =>
      agents.map(agent => {
        const eligibility = getEligibility(agent.intent_workflow_id);
        return {
          disabled: isCheckingVersionHistories || eligibility.disabled,
          label: agent.intent_name,
          optionEndAdornment:
            isCheckingVersionHistories || eligibility.disabledReason ? (
              <Tooltip
                tooltipContent={
                  isCheckingVersionHistories
                    ? t('variantsSection.checkingAvailability')
                    : eligibility.disabledReason
                }
              >
                <Box
                  component='span'
                  sx={{ display: 'inline-flex', ml: 1, pointerEvents: 'auto' }}
                >
                  <InfoOutlined sx={{ color: 'grey.400', fontSize: 16 }} />
                </Box>
              </Tooltip>
            ) : undefined,
          value: agent.intent_workflow_id,
        };
      }),
    [agents, getEligibility, isCheckingVersionHistories, t],
  );

  const controlVariant = variants?.find(v => v.is_control);
  const nonControlVariants = variants?.filter(v => !v.is_control) || [];
  const canAddVariant = nonControlVariants.length < 2;

  const { handleAbTestConfigUpdate } = useDebouncedUpdateAbTestConfig();

  const handleVersionDropdownChange = (
    version: number,
    versionType: WorkflowVersionType,
  ) => {
    if (!data) {
      return;
    }

    const updatedVariants = buildControlVariantUpdate(
      data.variants ?? [],
      version,
      versionType,
      controlVariant?.traffic_percentage || 50,
    );

    handleAbTestConfigUpdate(
      generateAbTestConfigRequestBody(data, {
        variants: updatedVariants,
      }),
      { immediate: true },
    );
  };

  // Handler for non-control variant dropdown changes (Variant A or B when not draft)
  // variantIndex: 0 for Variant A, 1 for Variant B
  const handleNonControlVariantDropdownChange = (
    variantIndex: number,
    version: number,
    versionType: WorkflowVersionType,
  ) => {
    if (!data) {
      return;
    }

    const existingVariant = nonControlVariants[variantIndex];
    const dataVariantsIndex = variantIndex + 1; // Control is at index 0
    const newVariant = {
      intent_workflow_version: version,
      is_control: false,
      name: existingVariant?.name || VARIANT_DEFAULT_NAMES[dataVariantsIndex],
      traffic_percentage: existingVariant?.traffic_percentage ?? 50,
      version_type: versionType,
    };

    const currentVariants = data.variants ?? [];

    const updatedVariants = existingVariant
      ? currentVariants.map((v, index) =>
          !v.is_control && index === dataVariantsIndex ? newVariant : v,
        )
      : [...currentVariants, newVariant];

    handleAbTestConfigUpdate(
      generateAbTestConfigRequestBody(data, {
        variants: updatedVariants,
      }),
      { immediate: true },
    );
  };

  // Handler for non-control variant traffic changes (Variant A or B when not draft)
  // variantIndex: 0 for Variant A, 1 for Variant B
  const handleNonControlVariantTrafficChange = (
    variantIndex: number,
    trafficValue: string,
  ) => {
    const trafficPercentage = parseTrafficPercentage(trafficValue);
    if (!data || trafficPercentage === null) {
      return;
    }

    const existingVariant = nonControlVariants[variantIndex];
    const dataVariantsIndex = variantIndex + 1; // Control is at index 0

    const newVariant = {
      intent_workflow_version: existingVariant?.intent_workflow_version || 0,
      is_control: false,
      name: existingVariant?.name || VARIANT_DEFAULT_NAMES[dataVariantsIndex],
      traffic_percentage: trafficPercentage,
      version_type: 'saved' as const,
    };

    const updatedVariants = existingVariant
      ? data.variants?.map((v, index) =>
          !v.is_control && index === dataVariantsIndex
            ? { ...v, traffic_percentage: trafficPercentage }
            : v,
        ) || []
      : [...(data.variants || []), newVariant];

    handleAbTestConfigUpdate(
      generateAbTestConfigRequestBody(data, {
        variants: updatedVariants,
      }),
    );
  };

  const handleAddVariant = () => {
    if (!data) {
      return;
    }

    // If no Variant A exists, create Variant A and Variant B; otherwise just Variant B
    const hasVariantA = nonControlVariants[VARIANT_A_INDEX];
    const variantsToAdd = hasVariantA
      ? [
          {
            intent_workflow_version: 0,
            is_control: false,
            name: VARIANT_DEFAULT_NAMES[2],
            traffic_percentage: 0,
            version_type: 'saved' as const,
          },
        ]
      : [
          {
            intent_workflow_version: 0,
            is_control: false,
            name: VARIANT_DEFAULT_NAMES[1],
            traffic_percentage: 0,
            version_type: 'saved' as const,
          },
          {
            intent_workflow_version: 0,
            is_control: false,
            name: VARIANT_DEFAULT_NAMES[2],
            traffic_percentage: 0,
            version_type: 'saved' as const,
          },
        ];

    handleAbTestConfigUpdate(
      generateAbTestConfigRequestBody(data, {
        variants: [...(data.variants || []), ...variantsToAdd],
      }),
      { immediate: true },
    );
  };

  const handleRemoveVariant = (variantIndex: number) => {
    if (!data) {
      return;
    }

    const dataVariantsIndex = variantIndex + 1; // Control is at index 0

    const updatedVariants =
      data.variants?.filter(
        (v, index) => !(!v.is_control && index === dataVariantsIndex),
      ) || [];

    handleAbTestConfigUpdate(
      generateAbTestConfigRequestBody(data, {
        variants: updatedVariants,
      }),
      { immediate: true },
    );
  };

  if (isEditingDisabled) {
    const versionHistory =
      allVersionHistories?.[intent_workflow_id ?? ''] ?? [];

    const getVariantVersionLabel = (variant: SolveABTestVariant) =>
      getVariantVersionLabelUtil(variant, versionHistory);

    const totalTraffic =
      variants?.reduce((sum, v) => sum + (v.traffic_percentage || 0), 0) ?? 0;

    const variantBadgeColors: Record<number, string> = {
      0: palette.chart[1],
      1: palette.chart[2],
    };

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        <ReadOnlyField
          label={t('variantsSection.totalTraffic')}
          value={t('variantsSection.totalTrafficValue', { totalTraffic })}
        />
        {controlVariant && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Chip
              color={palette.colors.white}
              fontSize='12px'
              label={controlVariant.name ?? t('variantsSection.control')}
              size='small'
              sx={{
                backgroundColor: palette.colors.teal[500],
                fontWeight: 600,
                width: 'fit-content',
              }}
              variant='filled'
            />
            <Typography variant='font14'>
              {t('variantsSection.versionWithTraffic', {
                percentage: controlVariant.traffic_percentage,
                versionLabel: getVariantVersionLabel(controlVariant),
              })}
            </Typography>
          </Box>
        )}
        {nonControlVariants.map((variant, index) => (
          <Box
            key={index}
            sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
          >
            <Chip
              color={palette.colors.white}
              fontSize='12px'
              label={
                variant.name ??
                t('variantsSection.variantLetter', {
                  letter: String.fromCharCode(65 + index),
                })
              }
              size='small'
              sx={{
                backgroundColor: variantBadgeColors[index] ?? palette.chart[1],
                fontWeight: 600,
                width: 'fit-content',
              }}
              variant='filled'
            />
            <Typography variant='font14'>
              {t('variantsSection.versionWithTraffic', {
                percentage: variant.traffic_percentage,
                versionLabel: getVariantVersionLabel(variant),
              })}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
      }}
    >
      {/* Control Variant */}
      <VariantRow
        agentOptions={agentOptions}
        badgeColor={palette.colors.teal[500]}
        badgeLabel={t('variantsSection.control')}
        description={t('variantsSection.controlDescription')}
        intentWorkflowId={intent_workflow_id ?? undefined}
        isLoadingAgents={isCheckingVersionHistories}
        isTotalTrafficPercentageValid={Boolean(
          data && getIsTotalTrafficPercentageValid(data),
        )}
        onAgentChange={(workflowId: string) => {
          if (!data) {
            return;
          }

          const versionHistory = allVersionHistories?.[workflowId] ?? [];
          const workflowVersionHistoryData =
            getVersionHistoryDataForIntentWorkflowId(
              workflowId,
              versionHistory,
            );

          const controlVariantData = {
            intent_workflow_version: workflowVersionHistoryData?.version ?? 0,
            is_control: true,
            name: VARIANT_DEFAULT_NAMES[0],
            traffic_percentage: controlVariant?.traffic_percentage || 50,
            version_type:
              workflowVersionHistoryData?.version_type ??
              ('published' as const),
          };

          // Create control variant and a default Variant A
          const variants = [
            controlVariantData,
            {
              intent_workflow_version: 0,
              is_control: false,
              name: VARIANT_DEFAULT_NAMES[1],
              traffic_percentage: 50,
              version_type: 'saved' as const,
            },
          ];

          handleAbTestConfigUpdate(
            generateAbTestConfigRequestBody(data, {
              intent_workflow_id: workflowId,
              variants,
            }),
            { immediate: true },
          );
        }}
        onTextInputChange={(trafficValue: string) => {
          const trafficPercentage = parseTrafficPercentage(trafficValue);
          if (!data || trafficPercentage === null) {
            return;
          }

          const controlVariant = data.variants?.find(v => v.is_control);

          const newVariant = {
            intent_workflow_version:
              controlVariant?.intent_workflow_version || 0,
            is_control: true,
            name: VARIANT_DEFAULT_NAMES[0],
            traffic_percentage: trafficPercentage,
            version_type: 'published' as const,
          };

          const variants = controlVariant
            ? data.variants?.map(variant => {
                if (variant.is_control) {
                  return {
                    ...variant,
                    traffic_percentage: trafficPercentage,
                  };
                }
                return variant;
              }) || []
            : [newVariant, ...(data.variants || [])];

          handleAbTestConfigUpdate(
            generateAbTestConfigRequestBody(data, {
              variants,
            }),
          );
        }}
        onVersionChange={handleVersionDropdownChange}
        placeholder={t('variantsSection.controlPlaceholder')}
        publishedOnly
        readOnly={isEditingDisabled}
        selectedAgentId={intent_workflow_id ?? undefined}
        selectedVersion={controlVariant?.intent_workflow_version}
        selectedVersionType={
          controlVariant?.version_type as WorkflowVersionType
        }
        trafficValue={controlVariant?.traffic_percentage?.toString() || '0'}
      />
      {/* Variant A - Always visible */}
      <VariantRow
        badgeColor={palette.chart[1]}
        badgeLabel={t('variantsSection.variantABadge')}
        description={t('variantsSection.variantDescription')}
        hideLiveVersion
        intentWorkflowId={intent_workflow_id ?? undefined}
        isDuplicateVersion={getIsDuplicateVariantVersion(
          nonControlVariants[VARIANT_A_INDEX],
          [controlVariant, nonControlVariants[VARIANT_B_INDEX]],
        )}
        isTotalTrafficPercentageValid={Boolean(
          data && getIsTotalTrafficPercentageValid(data),
        )}
        onRemove={
          canAddVariant ? undefined : () => handleRemoveVariant(VARIANT_A_INDEX)
        }
        onTextInputChange={trafficValue =>
          handleNonControlVariantTrafficChange(VARIANT_A_INDEX, trafficValue)
        }
        onVersionChange={(version, versionType) =>
          handleNonControlVariantDropdownChange(
            VARIANT_A_INDEX,
            version,
            versionType,
          )
        }
        placeholder={t('variantsSection.selectVersionPlaceholder')}
        readOnly={!intent_workflow_id || isEditingDisabled}
        selectedVersion={
          nonControlVariants[VARIANT_A_INDEX]?.intent_workflow_version
        }
        selectedVersionType={
          nonControlVariants[VARIANT_A_INDEX]
            ?.version_type as WorkflowVersionType
        }
        trafficValue={
          nonControlVariants[VARIANT_A_INDEX]?.traffic_percentage?.toString() ||
          '0'
        }
      />
      {/* Variant B - Renders from backend state */}
      {nonControlVariants[VARIANT_B_INDEX] && (
        <VariantRow
          badgeColor={palette.chart[2]}
          badgeLabel={t('variantsSection.variantBBadge')}
          description={t('variantsSection.variantDescription')}
          hideLiveVersion
          intentWorkflowId={intent_workflow_id ?? undefined}
          isDuplicateVersion={getIsDuplicateVariantVersion(
            nonControlVariants[VARIANT_B_INDEX],
            [controlVariant, nonControlVariants[VARIANT_A_INDEX]],
          )}
          isTotalTrafficPercentageValid={Boolean(
            data && getIsTotalTrafficPercentageValid(data),
          )}
          onRemove={
            canAddVariant
              ? undefined
              : () => handleRemoveVariant(VARIANT_B_INDEX)
          }
          onTextInputChange={trafficValue =>
            handleNonControlVariantTrafficChange(VARIANT_B_INDEX, trafficValue)
          }
          onVersionChange={(version, versionType) =>
            handleNonControlVariantDropdownChange(
              VARIANT_B_INDEX,
              version,
              versionType,
            )
          }
          placeholder={t('variantsSection.selectVersionPlaceholder')}
          readOnly={!intent_workflow_id || isEditingDisabled}
          selectedVersion={
            nonControlVariants[VARIANT_B_INDEX]?.intent_workflow_version
          }
          selectedVersionType={
            nonControlVariants[VARIANT_B_INDEX]
              ?.version_type as WorkflowVersionType
          }
          trafficValue={
            nonControlVariants[
              VARIANT_B_INDEX
            ]?.traffic_percentage?.toString() || '0'
          }
        />
      )}
      {/* Add Variant Button */}
      <Box>
        <Button
          disabled={!intent_workflow_id || !canAddVariant || isEditingDisabled}
          onClick={handleAddVariant}
          variant='secondary'
        >
          {t('variantsSection.addVariantButton')}
        </Button>
      </Box>
    </Box>
  );
};

interface VariantRowProps {
  badgeColor: string;
  badgeLabel: string;
  description: string;
  /** For agent selection dropdown (Control row) */
  agentOptions?: { category?: string; label: string; value: string }[];
  intentWorkflowId?: string;
  isDuplicateVersion?: boolean;
  /** Whether agent options are being loaded */
  isLoadingAgents?: boolean;
  isTotalTrafficPercentageValid: boolean;
  menuDescription?: string;
  /** For agent selection (Control row) */
  onAgentChange?: (workflowId: string) => void;
  onRemove?: () => void;
  onTextInputChange: (newValue: string) => void;
  /** For version selection */
  onVersionChange: (version: number, versionType: WorkflowVersionType) => void;
  placeholder?: string;
  /** Hide the live version from the version dropdown */
  hideLiveVersion?: boolean;
  /** Only show published versions in the version dropdown */
  publishedOnly?: boolean;
  readOnly?: boolean;
  selectedAgentId?: string;
  selectedVersion?: number;
  selectedVersionType?: WorkflowVersionType;
  trafficValue: string;
}

const VariantRow = ({
  agentOptions,
  badgeColor,
  badgeLabel,
  description,
  hideLiveVersion = false,
  intentWorkflowId,
  isDuplicateVersion = false,
  isLoadingAgents = false,
  isTotalTrafficPercentageValid,
  menuDescription,
  onAgentChange,
  onRemove,
  onTextInputChange,
  onVersionChange,
  placeholder,
  publishedOnly = false,
  readOnly = false,
  selectedAgentId,
  selectedVersion,
  selectedVersionType,
  trafficValue,
}: VariantRowProps) => {
  const { t } = useTranslation('agent-ab-test');
  const { palette } = useTheme();

  // Determine if this is a control row (has agent selection) or variant row (version only)
  const isControlRow = Boolean(agentOptions && onAgentChange);

  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        gap: '9px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flex: '1',
          flexDirection: 'column',
          gap: '8px',
        }}
      >
        {/* Badge and Description */}
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            gap: '8px',
          }}
        >
          <Chip
            color={palette.colors.white}
            fontSize='12px'
            label={badgeLabel}
            size='small'
            sx={{
              backgroundColor: badgeColor,
              fontWeight: 600,
            }}
            variant='filled'
          />
          <Typography color={palette.colors.grey[600]} variant='font12'>
            {description}
          </Typography>
        </Box>
        {/* Dropdown and Traffic Input */}
        <Box
          sx={{
            display: 'flex',
            gap: '8px',
          }}
        >
          {isControlRow ? (
            <>
              {/* Agent selection dropdown (Control row only) */}
              <SelectDropdown
                id={`variant-agent-select-${badgeLabel.toLowerCase().replace(' ', '-')}`}
                inputProps={{ readOnly }}
                isMenuSearchable
                menuDescription={menuDescription}
                onChange={e => onAgentChange?.(e.target.value)}
                options={agentOptions || []}
                placeholder={placeholder}
                shouldErrorMessageTakeUpSpace={false}
                topMenuChildren={
                  isLoadingAgents ? (
                    <Box
                      sx={{
                        alignItems: 'center',
                        display: 'flex',
                        gap: 1,
                        px: 2,
                        py: 1,
                      }}
                    >
                      <CircularProgress size={16} />
                      <Typography variant='font12'>
                        {t('variantsSection.checkingAvailability')}
                      </Typography>
                    </Box>
                  ) : undefined
                }
                value={selectedAgentId || ''}
              />
              {/* Version selection dropdown (Control row) */}
              <VersionSelectDropdown
                id={`variant-version-select-${badgeLabel.toLowerCase().replace(' ', '-')}`}
                intentWorkflowId={intentWorkflowId || ''}
                onChange={onVersionChange}
                placeholder={t('variantsSection.selectVersion')}
                publishedOnly={publishedOnly}
                readOnly={readOnly}
                selectedVersion={selectedVersion}
                selectedVersionType={selectedVersionType}
                skip={!intentWorkflowId}
              />
            </>
          ) : (
            /* Version selection dropdown (Variant A/B rows) */
            <VersionSelectDropdown
              error={
                isDuplicateVersion
                  ? t('variantsSection.versionAlreadySelected')
                  : undefined
              }
              hideLiveVersion={hideLiveVersion}
              id={`variant-version-select-${badgeLabel.toLowerCase().replace(' ', '-')}`}
              intentWorkflowId={intentWorkflowId || ''}
              onChange={onVersionChange}
              placeholder={placeholder}
              readOnly={readOnly}
              selectedVersion={selectedVersion}
              selectedVersionType={selectedVersionType}
              skip={!intentWorkflowId}
            />
          )}
        </Box>
      </Box>
      <Box
        sx={{
          alignSelf: 'flex-end',
          position: 'relative',
          width: '110px',
        }}
      >
        <TextField
          aria-label={t('variantsSection.trafficPercentageAriaLabel', {
            badge: badgeLabel,
          })}
          endAdornment={
            <Typography color={palette.colors.grey[600]} variant='font14'>
              {t('variantsSection.percentSign')}
            </Typography>
          }
          error={
            !selectedVersion
              ? undefined
              : Number(trafficValue) > 0
                ? isTotalTrafficPercentageValid
                  ? undefined
                  : t('variantsSection.sumNot100')
                : t('variantsSection.mustBeGreaterThanZero')
          }
          label={t('variantsSection.trafficLabel')}
          onChange={e => {
            onTextInputChange(e.target.value);
          }}
          shouldErrorMessageTakeUpSpace={false}
          value={trafficValue}
        />
      </Box>
      {onRemove && !readOnly && (
        <IconButton
          aria-label={t('variantsSection.removeVariantAriaLabel', {
            badge: badgeLabel,
          })}
          disabled={readOnly}
          onClick={onRemove}
          style={{ alignSelf: 'end', padding: '19px' }}
          variant='ghost'
        >
          <IconTrash size={20} />
        </IconButton>
      )}
    </Box>
  );
};

export default AgentVariantsSection;

const ReadOnlyField = ({ label, value }: { label: string; value: string }) => {
  const { palette } = useTheme();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      <Typography color={palette.colors.grey[600]} variant='font12'>
        {label}
      </Typography>
      <Typography variant='font14'>{value}</Typography>
    </Box>
  );
};
